import { useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { identity, noop } from "lodash"
import useResizeObserver from "hooks/resize-observer"
import MultiLine from "components/charts/multi-line"
import AnalyzeTooltip from "components/analyze-tooltip"
import { smallPercentageFormatter } from "helpers/formatters/numbers"
import { useMultiLineHandlers } from "./hooks/use-multiline-handlers"
import multiDateContract from "gatherers/multi-date/contract"
import { GraphLoadingState, GraphEmptyState } from "./components/graph-states"
import GraphErrorFallback from "./components/graph-error-fallback"
import ScrollableLegend from "./components/scrollable-legend"
import LegendItem from "components/charts/multi-line/legend-item"
import { LOADING, ERROR, OK } from "helpers/data-statuses"
import { MULTI_LINE_ITEM_SHAPE } from "./prop-types"
import { THRESHOLD_PARAMS } from "helpers/threshold"
import { ErrorBoundary } from "@sentry/react"

import "./styles.scss"

const EMPTY_ARRAY = []

function MultiLineGraphWithError({ status, errorMessage, ...props }) {
  // Leverage ErrorBoundary to catch errors and display a fallback component
  if (status === ERROR) {
    throw new Error(errorMessage)
  }

  return <MultiLineGraphComponent {...props} />
}

MultiLineGraphWithError.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  errorMessage: PropTypes.string,
}

function MultiLineGraphComponent({
  data,
  endDate,
  startDate,
  items = EMPTY_ARRAY,
  filterItems = identity,
  setItems = noop,
  setOnlyItem = noop,
  enableItems = noop,
  emptyMessage,
  noDataMessage,
  formatter = smallPercentageFormatter,
  thresholdParams,
  onDotClick,
  // Owned by the parent template (isUsingIntradayQuery), which also excludes a pinned batch — don't
  // recompute it here without that condition or a batched same-day view gets intraday cursor behavior.
  isOneDayMode = false,
}) {
  const filteredItems = useMemo(() => filterItems(items, data), [filterItems, items, data])

  const graphRef = useRef(null)
  const [width, height] = useResizeObserver(graphRef)

  const numYTicks = useMemo(() => {
    return Math.min(Math.max(Math.floor(height / 55), 3), 6)
  }, [height])

  const {
    toggle,
    toggleOnly,
    focusChartLine,
    blurChartLine,
    handleSetDate,
    isOnlyMode,
    onlyLegendText,
    focusedChartLine,
  } = useMultiLineHandlers(filteredItems, setItems, setOnlyItem, enableItems, null)

  // Distinguish "the query returned zero rows" (e.g. intraday data genuinely not available yet)
  // from "rows exist but nothing in the current selection has a value" (e.g. disabled items,
  // a category with no matching factors) — these need different messages.
  if (!data || data.length === 0) {
    return <GraphEmptyState message={noDataMessage ?? emptyMessage} />
  }

  if (!filteredItems || filteredItems.length === 0) {
    return <GraphEmptyState message={emptyMessage} />
  }

  return (<div className="graph-content">
    {data && (
      <div className="enhanced-multi-line-container">
        <div className="graph-top">
          <ScrollableLegend className="legend">
            {filteredItems.map(item =>
              <LegendItem
                key={item.id}
                {...item}
                onlyLegendText={onlyLegendText}
                onClick={toggle}
                onOnlyToggle={toggleOnly}
                focus={focusChartLine}
                blur={blurChartLine}
                focused={focusedChartLine}
              />,
            )}
          </ScrollableLegend>
        </div>
        <MultiLine
          data={data}
          resizableRef={graphRef}
          items={filteredItems}
          startDate={startDate}
          endDate={endDate}
          setDate={handleSetDate}
          toggle={toggle}
          toggleOnly={toggleOnly}
          focusChartLine={focusChartLine}
          blurChartLine={blurChartLine}
          focusedChartLine={focusedChartLine}
          onlyItem={isOnlyMode}
          tickFormat={formatter}
          numYTicks={numYTicks}
          thresholdParams={thresholdParams}
          width={width}
          height={height}
          onDotClick={onDotClick}
          isOneDayMode={isOneDayMode}
          tooltip={
            <AnalyzeTooltip
              data={data}
              items={filteredItems}
              focusedChartLine={focusedChartLine}
              formatter={formatter}
              isOneDayMode={isOneDayMode}
              hasDotClick={Boolean(onDotClick)}
            />
          }
        />
      </div>
    )}
  </div>)
}

MultiLineGraphComponent.propTypes = {
  data: multiDateContract.isRequired,
  endDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(MULTI_LINE_ITEM_SHAPE).isRequired,
  filterItems: PropTypes.func,
  setItems: PropTypes.func,
  setOnlyItem: PropTypes.func,
  enableItems: PropTypes.func,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  formatter: PropTypes.func,
  thresholdParams: THRESHOLD_PARAMS,
  onDotClick: PropTypes.func,
  isOneDayMode: PropTypes.bool,
}

export default function MultiLineGraph({ status, loadingMessage = "", ...props }) {
  if (status === LOADING) {
    return <GraphLoadingState message={loadingMessage} />
  }

  return (
    <ErrorBoundary fallback={GraphErrorFallback}>
      <MultiLineGraphWithError {...props} />
    </ErrorBoundary>
  )
}

MultiLineGraph.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  data: multiDateContract,
  endDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(MULTI_LINE_ITEM_SHAPE).isRequired,
  filterItems: PropTypes.func,
  setItems: PropTypes.func,
  setOnlyItem: PropTypes.func,
  enableItems: PropTypes.func,
  loadingMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  formatter: PropTypes.func,
  thresholdParams: THRESHOLD_PARAMS,
  onDotClick: PropTypes.func,
  isOneDayMode: PropTypes.bool,
}
